import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Strings from '../Strings';
import Wizard from '../containers/Modal/Wizard';
import { Input, SelectField } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { actions as patientActions } from '../pages/SuperUser/Patients/redux/actions';
import { notificationActions } from '../components/Notification/redux/actions';
import {
  CREATE_CUSTOM_TASK_RESULT,
  DISABLE_ORG_TASK_RESULT,
  ENABLE_ORG_TASK_RESULT,
  GET_TIMER_DEFAULT_TASK_RESULT,
  SET_TIMER_DEFAULT_TASK_ERROR,
  UPDATE_CUSTOM_TASK_RESULT,
  UPDATE_GLOBAL_TASK_DEFAULTS_RESULT,
} from '../pages/SuperUser/Patients/redux/constants';

export const CUSTOM_WORK_TYPE_MODAL_NAME = 'custom-work-type-modal';
const NEW_TASK_ID_PLACEHOLDER = 'new_task_id_placeholder';

function CustomWorkTypeModal(props) {
  const { data, createCustomTask, updateCustomTask, updateGlobalTaskDefaults, enableOrgTask, disableOrgTask, setTimerDefaultTask, getTimerDefaultTask } = props;
  const { onCancel, showNotification, closeModalWithNextAction } = props;
  const { allTasks, orgTasks, orgId } = data || {};
  const isEditMode = data?.mode !== 'create';
  const task = data?.task || {id: NEW_TASK_ID_PLACEHOLDER};
  const isTaskDefault = Boolean(task.isDefault ?? task.default);
  const canEditTask = !isEditMode || Boolean(task.orgSpecific || (data?.mode === 'editGenericTask' && task.editable));

  const [title, setTitle] = React.useState(task.taskName || '');
  const [sequence, setSequence] = React.useState(task.sequence ?? '');
  const [isDefault, setIsDefault] = React.useState(isTaskDefault);
  // Tasks that don't track time (e.g. setup/education calls) are logged without a duration.
  const initialAllowDuration = task.allowDuration !== false;
  const [allowDuration, setAllowDuration] = React.useState(initialAllowDuration);
  const [timerDefaultTaskId, setTimerDefaultTaskId] = React.useState(task.isTimerDefault ? task.id : null);
  const [previousTimerDefault, setPreviousTimerDefault] = React.useState(task.isTimerDefault === true ? task.id : null);
  const [newOrgTasks, setNewOrgTasks] = React.useState(orgTasks || []);
  const [isSaving, setIsSaving] = React.useState(false);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [forceNameChange, setForceNameChange] = React.useState(false);

  React.useEffect(() => {
    if (orgId) {
      getTimerDefaultTask(orgId).then(resp => {
        if (resp?.type === GET_TIMER_DEFAULT_TASK_RESULT) {
          const timerDefault = resp?.response?.taskMetadataId;
          setTimerDefaultTaskId(timerDefault);
          setPreviousTimerDefault(timerDefault);
        }
      });
    }
  }, [getTimerDefaultTask, isEditMode, orgId, task.id]);

  React.useEffect(() => {
    const errors = [];
    const sequenceValue = String(sequence ?? '').trim();
    if (sequenceValue !== '' && (isNaN(sequenceValue) || !Number.isInteger(Number(sequenceValue)))) {
      errors.push({
        property: 'sequence',
        propertyName: Strings.sequence,
        errors: [Strings.sequenceMustBeInteger],
        missing: false,
      });
    }
    if (title.trim() === '') {
      errors.push({
        property: 'title',
        propertyName: Strings.capPatient.taskName,
        missing: true,
      });
    }
    if (previousTimerDefault === task.id && !timerDefaultTaskId) {
      errors.push({
        property: 'timer-default',
        propertyName: Strings.newTimerDefaultTask,
        missing: true,
      });
    }
    setValidationErrors(errors);
  }, [sequence, title, timerDefaultTaskId, previousTimerDefault, task.id]);

  const submit = async () => {
    if (isSaving) return;
    if (!title.trim()) return;

    const sequenceValue = String(sequence).trim();
    const parsedSequence = sequenceValue === '' ? undefined : Number(sequenceValue);

    const payload = {
      title: title.trim(),
      sequence: parsedSequence,
      default: orgId === null ? isDefault : undefined,
      org_uuid: orgId === null ? undefined : orgId,
      allow_duration: allowDuration,
      ...(isEditMode && title.trim() !== data?.task?.taskName && { force_used_name_change: forceNameChange }),
    };

    setIsSaving(true);

    const metadataChanged = title.trim() !== task.taskName
      || (parsedSequence ?? null) !== (task.sequence ?? null)
      || allowDuration !== initialAllowDuration;
    const defaultChanged = orgId === null && isDefault !== isTaskDefault;
    const defaultOnly = isEditMode && orgId === null && !metadataChanged && defaultChanged;
    let resp;
    if (!isEditMode || metadataChanged || defaultChanged) {
      resp = defaultOnly
        ? await updateGlobalTaskDefaults({task_ids: [task.id], default: isDefault})
        : isEditMode
          ? await updateCustomTask({ ...payload, task_id: task.id })
          : await createCustomTask(payload);
      const okType = defaultOnly ? UPDATE_GLOBAL_TASK_DEFAULTS_RESULT
        : isEditMode ? UPDATE_CUSTOM_TASK_RESULT : CREATE_CUSTOM_TASK_RESULT;
      if (resp?.type !== okType) {
        showNotification(resp?.response?.data?.standard_error?.message || Strings.unableToSaveWorkType, 5000, true);
        setIsSaving(false);
        return;
      }
    }

    let success = true;

    // The checkbox only toggles this task, so enable/disable just this one: a full-list save
    // built from the list this modal opened with would undo concurrent edits of other tasks.
    const wasEnabledForOrg = (orgTasks || []).some(t => t.id === task.id);
    const isEnabledForOrg = newOrgTasks.some(t => t.id === task.id);
    if (wasEnabledForOrg !== isEnabledForOrg) {
      const savedTaskId = task.id === NEW_TASK_ID_PLACEHOLDER ? resp?.response?.task_id : task.id;
      const orgTaskResp = isEnabledForOrg
        ? await enableOrgTask(orgId, savedTaskId)
        : await disableOrgTask(orgId, savedTaskId);
      if (orgTaskResp?.type !== (isEnabledForOrg ? ENABLE_ORG_TASK_RESULT : DISABLE_ORG_TASK_RESULT)) {
        showNotification(orgTaskResp?.response?.data?.standard_error?.message || 'Unable to update organization tasks', 5000, true);
        success = false;
      }
    }

    const newIdForTimerDefault = timerDefaultTaskId === NEW_TASK_ID_PLACEHOLDER ? resp?.response?.task_id : timerDefaultTaskId;
    if (newIdForTimerDefault !== previousTimerDefault) {
      const timerResp = await setTimerDefaultTask(orgId, {taskMetadataId: newIdForTimerDefault});
      if (timerResp?.type === SET_TIMER_DEFAULT_TASK_ERROR) {
        showNotification(timerResp?.response?.data?.standard_error?.message || 'Unable to set timer default task', 5000, true);
        success = false;
      }
    }

    if (success) {
      showNotification(Strings.formatString(Strings.itemSaved, Strings.workType));
      closeModalWithNextAction();
    }
    setIsSaving(false);
  };

  const protectedFieldLabel = label => orgId === null && !canEditTask ? (
    <span data-tooltip-id="tooltip" data-tooltip-content={Strings.systemWorkTypeMetadataExplanation}>
      {label}
    </span>
  ) : label;

  const isTaskSetPreviouslyAsTimerDefault = previousTimerDefault && (!previousTimerDefault || previousTimerDefault === task.id);
  const pages = [
    {
      id: 'custom-work-type',
      title: isEditMode ? Strings.editWorkType : Strings.addWorkType,
      content: (
        <>
          <Input
            name="title"
            id="title"
            label={protectedFieldLabel(Strings.capPatient.taskName)}
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            disabled={!canEditTask}
          />
          <Input
            name="sequence"
            id="sequence"
            label={protectedFieldLabel(Strings.sequence)}
            type="text"
            value={sequence}
            onChange={e => setSequence(e.target.value)}
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'sequence')?.errors || []}
            disabled={!canEditTask}
          />
          <Input
            name="allow_duration"
            id="allow_duration"
            label={protectedFieldLabel(Strings.tracksTime)}
            type="checkbox"
            checked={allowDuration}
            onChange={e => setAllowDuration(e.target.checked)}
            rowClassName="checkbox-first"
            disabled={!canEditTask}
          />
          {orgId === null && (
            <Input
              name="default"
              id="default"
              label={(
                <span data-tooltip-id="tooltip" data-tooltip-content={Strings.globalTaskDefaultExplanation}>
                  {Strings.globalTaskDefault}
                </span>
              )}
              type="checkbox"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              rowClassName="checkbox-first"
            />
          )}
          {orgId !== null && (
            <>
              <Input
                name="task_enabled_for_org"
                id="task_enabled_for_org"
                label={Strings.enabledForOrg}
                type="checkbox"
                checked={newOrgTasks.some(t => t.id === task.id)}
                disabled={!!task.mandatory}
                onChange={e => {
                  if (task.mandatory) return;
                  if (e.target.checked) {
                    setNewOrgTasks([...newOrgTasks, task]);
                  } else {
                    setNewOrgTasks(newOrgTasks.filter(t => t.id !== task.id));
                    if (timerDefaultTaskId === task.id) {
                      setTimerDefaultTaskId(null);
                    }
                  }
                }}
                rowClassName="checkbox-first"
              />
              {task.mandatory && <span>{Strings.cannotDisableMandatoryTasks}</span>}
              <div style={{ position: 'relative' }}>
                <Input
                  name="timer-default"
                  id="timer-default"
                  label={Strings.isTimerDefaultTask}
                  type="checkbox"
                  checked={timerDefaultTaskId === task.id}
                  onChange={e => setTimerDefaultTaskId(e.target.checked ? task.id : null)}
                  rowClassName="checkbox-first"
                  disabled={!newOrgTasks.some(t => t.id === task.id) || !allowDuration}
                />
                {!isTaskSetPreviouslyAsTimerDefault && (
                  <div
                    className="signal-alert"
                    data-tooltip-content={
                      Strings.formatString(
                        Strings.previousTimerDefaultTaskWarning, 
                        allTasks.find(t => t.id === previousTimerDefault)?.taskName || 'unknown task'
                      )
                    }
                    data-tooltip-id="tooltip"
                  />
                )}
              </div>
              {(!timerDefaultTaskId || timerDefaultTaskId !== task.id) && isTaskSetPreviouslyAsTimerDefault && isEditMode && (
                <SelectField
                  name="timer-default"
                  id="timer-default"
                  label={Strings.newTimerDefaultTask}
                  value={timerDefaultTaskId}
                  onChange={option => setTimerDefaultTaskId(option?.value)}
                  data={orgTasks.map(t => ({ value: t.id, label: t.taskName }))}
                  isSearchable
                  searchByLabelOnly
                  isRequired
                  highlightInvalid={highlightInvalidFields}
                />
              )}
              {isEditMode && (
                <Input
                  name="force-name-change"
                  id="force-name-change"
                  label="Force change task name"
                  type="checkbox"
                  checked={forceNameChange}
                  onChange={(e) => setForceNameChange(e.target.checked)}
                  rowClassName="checkbox-first"
                />
              )}
            </>
          )}
        </>
      ),
      emptyFieldsCount: validationErrors.filter(e => e.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];

  return (
    <Wizard
      name={CUSTOM_WORK_TYPE_MODAL_NAME}
      pages={pages}
      onSubmit={submit}
      onCancel={onCancel}
      onNextButtonHover={e => setHighlightMissingFields(e)}
    />
  );
}

CustomWorkTypeModal.propTypes = {
  data: PropTypes.shape({
    mode: PropTypes.oneOf(['create', 'edit', 'editGenericTask']),
    orgId: PropTypes.string,
    task: PropTypes.shape({
      id: PropTypes.number,
      taskName: PropTypes.string,
      sequence: PropTypes.number,
      isDefault: PropTypes.bool,
      default: PropTypes.bool,
      editable: PropTypes.bool,
      orgSpecific: PropTypes.bool,
      allowDuration: PropTypes.bool,
      org_uuid: PropTypes.string,
    }),
    allTasks: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      taskName: PropTypes.string,
    })),
    onSuccess: PropTypes.func,
  }),
  createCustomTask: PropTypes.func.isRequired,
  updateCustomTask: PropTypes.func.isRequired,
  updateGlobalTaskDefaults: PropTypes.func.isRequired,
  enableOrgTask: PropTypes.func.isRequired,
  disableOrgTask: PropTypes.func.isRequired,
  setTimerDefaultTask: PropTypes.func.isRequired,
  getTimerDefaultTask: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  closeModalWithNextAction: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  createCustomTask: payload => dispatch(patientActions.createCustomTask(payload)),
  updateCustomTask: payload => dispatch(patientActions.updateCustomTask(payload)),
  updateGlobalTaskDefaults: payload => dispatch(patientActions.updateGlobalTaskDefaults(payload)),
  enableOrgTask: (orgId, taskId) => dispatch(patientActions.enableOrgTask(orgId, taskId)),
  disableOrgTask: (orgId, taskId) => dispatch(patientActions.disableOrgTask(orgId, taskId)),
  setTimerDefaultTask: (orgId, data) => dispatch(patientActions.setTimerDefaultTask(orgId, data)),
  getTimerDefaultTask: orgId => dispatch(patientActions.getTimerDefaultTask(orgId)),
  onCancel: () => dispatch(closeModal(CUSTOM_WORK_TYPE_MODAL_NAME)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  closeModalWithNextAction: () => {
    dispatch(closeModal(CUSTOM_WORK_TYPE_MODAL_NAME));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(CustomWorkTypeModal);
